import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { GetCampaignTypeLabel } from '../../constants/campaignTypes';
import api from '../../services/api';
import { FormatDateTimeBr, Pad2 } from '../../utils/date';
import { Clamp, FormatDurationHuman, FormatMoney } from '../../utils/format';
import { Skeleton } from '../ui/Skeleton';
import { DASH, FormatPercentOrDash, IsNum } from './campaignCardShared';

/**
 * Linha do tempo MENSAL de campanhas do anúncio.
 *
 * O mês é uma sequência de trechos e cada trecho pertence a UMA campanha: a
 * fonte é o /items/:id/sale_price do ML, onde só existe uma promoção valendo
 * por vez. A tela desenha uma linha por campanha e, nela, uma barra por
 * período em que a campanha valeu dentro do mês — a mesma campanha pode
 * reaparecer várias vezes (Especial 01–10, Mega 11–14, Especial 15–18…).
 * Onde não há barra, não havia campanha (preço cheio): não existe linha para
 * "sem campanha".
 *
 * A largura da barra é EXATAMENTE a duração do período sobre o calendário,
 * sem min-width — com min-width a barra mentiria sobre o calendário. O
 * backend já recorta cada trecho ao mês pedido e entrega posições em dias
 * decimais (`start`/`end`) para a geometria e dias inteiros
 * (`start_day`/`end_day`) para o rótulo; aqui só há projeção em %.
 *
 * No MÊS ATUAL o eixo termina em HOJE (pedido do dono: "hoje é dia 19, não
 * precisa aparecer setembro inteiro"): a régua vai de 01 ao dia de hoje, e o
 * período em andamento encosta na borda direita, que é "agora". Meses
 * passados continuam com o mês inteiro.
 *
 * O filtro é um seletor com os meses que existem: do mês atual (o padrão, no
 * topo da lista) até aquele em que a série do anúncio começou
 * (`recorded_since`) — antes disso não há registro e depois não há calendário. O card leva os valores dentro (consumer, líquido,
 * rebate) enquanto couberem; a informação completa — datas e horas, duração,
 * "de R$", sua parte — fica na visão "Dados".
 *
 * A mesma informação também é lida em TABELA ("Dados"), uma linha por período
 * em ordem cronológica: é a tabela da aba Acompanhamento aplicada à lógica
 * daqui — só a campanha que estava valendo, não as candidaturas.
 */

const SAO_PAULO = 'America/Sao_Paulo';

// Ano, mês e dia de um instante em Brasília. Não dá para usar getFullYear/
// getMonth/getDate locais: o navegador de quem abre pode estar em outro fuso,
// e o contrato do backend (recorte do mês, `today_day`, `recorded_since`) é
// em Brasília.
const DATE_PARTS_SP = new Intl.DateTimeFormat('en-US', {
    timeZone: SAO_PAULO,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

// Hora e minuto em Brasília, para a fração do dia (0,5 = meio-dia): é onde o
// eixo do mês atual termina enquanto o `now_day` do backend não chegou.
const TIME_PARTS_SP = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});
function saoPauloParts(date) {
    const parts = DATE_PARTS_SP.formatToParts(date);
    const valor = (tipo) => Number(parts.find((p) => p.type === tipo)?.value);
    const tempo = TIME_PARTS_SP.formatToParts(date);
    const hora = Number(tempo.find((p) => p.type === 'hour')?.value) || 0;
    const minuto = Number(tempo.find((p) => p.type === 'minute')?.value) || 0;
    return { year: valor('year'), month: valor('month'), day: valor('day'), fraction: (hora * 60 + minuto) / 1440 };
}

// Abreviação pt-BR do mês ("set."). O rótulo do chip tira o ponto e põe a
// inicial maiúscula → "Set 2026". Formata em UTC de propósito: só o mês importa.
const MONTH_SHORT = new Intl.DateTimeFormat('pt-BR', { month: 'short', timeZone: 'UTC' });

// No mês atual a régua termina em AGORA (posição decimal). No primeiro minuto
// do dia 1º o eixo teria largura zero, então o mínimo é 1,05 dias.
const MIN_AXIS_END = 1.05;

// Mês como inteiro contínuo (ano × 12 + mês − 1): somar/subtrair 1 vira o mês
// seguinte/anterior sem tratar a virada de ano à mão.
const toMonthIndex = (year, month) => year * 12 + (month - 1);
const fromMonthIndex = (index) => ({ year: Math.floor(index / 12), month: (index % 12) + 1 });

// Mês (índice contínuo) de um instante, em Brasília.
function monthIndexAt(date) {
    const { year, month } = saoPauloParts(date);
    return toMonthIndex(year, month);
}

// "YYYY-MM" — o parâmetro `month` do endpoint.
function monthKeyOf(index) {
    const { year, month } = fromMonthIndex(index);
    return `${year}-${Pad2(month)}`;
}

// Dias do mês calculados localmente: servem à régua enquanto a resposta não
// chegou (ou falhou), para o filtro e a régua não sumirem no carregamento.
function daysInMonthOf(index) {
    const { year, month } = fromMonthIndex(index);
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function monthLabelOf(index) {
    const { year, month } = fromMonthIndex(index);
    const abbr = MONTH_SHORT.format(new Date(Date.UTC(year, month - 1, 1))).replace('.', '');
    return `${abbr.charAt(0).toUpperCase()}${abbr.slice(1)} ${year}`;
}

// Geometria da spec: o dia 1 fica em 0% e o último dia do EIXO em 100%, então
// a escala é (dias − 1). Vale para a régua e para as barras.
const pctOf = (day, days) => ((day - 1) / Math.max(days - 1, 1)) * 100;

// Régua: 01 05 10 15 20 25 e o último dia do eixo (28/29/30/31, ou hoje no mês
// atual). Um rótulo fixo colado no último (hoje 16 → "15" e "16") ficaria
// ilegível, por isso só entram os menores que (eixo − 1): hoje 16 → 01 05 10 16.
const RULER_DAYS = [1, 5, 10, 15, 20, 25];
// `axisEnd` é onde o eixo termina: o último dia do mês (inteiro) nos meses
// passados, ou a posição decimal de AGORA no mês atual (19,5 = meio-dia do
// 19). `lastLabel` é o rótulo da ponta (30, ou 19 = hoje). O "01" entra
// sempre; as marcas regulares só se ficarem a mais de 1,5 dia do rótulo da
// ponta, para "15" e "16" não se sobreporem; no mês atual o rótulo de hoje
// fica na posição do dia (um pouco antes da borda, que é "agora").
const rulerDaysOf = (lastLabel) => {
    const meio = RULER_DAYS.filter((d) => d > 1 && d < lastLabel - 1.5);
    return lastLabel > 1 ? [1, ...meio, lastLabel] : [1];
};

// Quanto o card precisa medir para caber cada bloco de valor dentro dele
// ("CONSUMER R$ 349,00"): a largura do card é a duração do período e não pode
// crescer para caber texto, então o que não couber sai do card.
const CARD_PAD_PX = 20;
const VALOR_PX = 82;
const quantosValoresCabem = (widthPx) => Clamp(Math.floor((widthPx - CARD_PAD_PX) / VALOR_PX), 0, 3);

// Card estreito demais até para um valor (períodos de horas, que é o caso
// comum enquanto a série é nova): o rótulo vai para FORA, abaixo do card, e
// ocupa ~22% da trilha; o centro fica em clamp(11%, meio do período, 89%)
// para o texto não estourar as bordas.
const LABEL_HALF_PCT = 11;
const LABEL_MIN_PCT = LABEL_HALF_PCT;
const LABEL_MAX_PCT = 100 - LABEL_HALF_PCT;
const LABEL_WIDTH_PCT = 2 * LABEL_HALF_PCT;

const money = (value) => (IsNum(value) ? FormatMoney(Number(value)) : DASH);

// Identidade de um período na tela: a campanha mais o instante em que começou.
const runKey = (campaign, run) => `${campaign.campaign_id}@${run.from ?? run.start}`;

const delta = (valor, referencia) =>
    IsNum(valor) && IsNum(referencia) ? Math.round((Number(valor) - Number(referencia)) * 100) / 100 : null;

/**
 * O período clicado vira a referência e as OUTRAS campanhas do mês entram
 * como comparação: quanto o vendedor receberia a mais (ou a menos) se cada
 * uma delas estivesse valendo no lugar dela.
 *
 * Uma campanha pode ter vários períodos no mês com valores diferentes (o ML
 * recalcula o rebate); vale o mais RECENTE, que é o que ela oferece hoje. A
 * própria campanha da referência fica de fora — comparar com ela mesma não
 * responde nada.
 */
const compararCampanhas = (campaigns, chave) => {
    let referencia = null;
    for (const campaign of campaigns) {
        for (const run of campaign.runs ?? []) {
            if (runKey(campaign, run) === chave) {
                referencia = { campaign, run };
            }
        }
    }
    if (!referencia) {
        return null;
    }
    const outras = campaigns
        .filter((campaign) => campaign.campaign_id !== referencia.campaign.campaign_id)
        .map((campaign) => {
            const runs = Array.isArray(campaign.runs) ? campaign.runs : [];
            const run = runs.reduce(
                (mais, atual) => (Number(atual.start) > Number(mais.start) ? atual : mais),
                runs[0],
            );
            return run
                ? {
                      campaign,
                      run,
                      liquido: delta(run.liquido, referencia.run.liquido),
                      consumer: delta(run.consumer_price, referencia.run.consumer_price),
                      rebate: delta(run.rebate, referencia.run.rebate),
                  }
                : null;
        })
        .filter(Boolean)
        // A que mais rende para o vendedor primeiro: é a pergunta que o clique faz.
        .sort((a, b) => (b.liquido ?? Number.NEGATIVE_INFINITY) - (a.liquido ?? Number.NEGATIVE_INFINITY));
    return { referencia, outras };
};

// "01–10" (período de um dia só → "05"). A coluna esquerda junta os períodos
// com " · " e põe "/MM" só no último, como no exemplo do dono:
// "01–10 · 15–18 · 27–30/09". Dois períodos no MESMO dia (saiu às 10h, voltou
// às 12h) dariam "05 · 05": o rótulo é de dias, então repete uma vez só.
const runLabel = (run) =>
    run.start_day === run.end_day ? Pad2(run.start_day) : `${Pad2(run.start_day)}–${Pad2(run.end_day)}`;
const periodsLabel = (runs, month) => {
    const dias = runs.map(runLabel).filter((label, i, todos) => i === 0 || label !== todos[i - 1]);
    return `${dias.join(' · ')}/${Pad2(month)}`;
};

/**
 * Geometria dos cards de UMA campanha: um por período em que ela valeu no mês,
 * e onde cada valor cabe.
 *
 * `axisEnd` é onde o eixo termina: o último dia do mês nos meses passados,
 * AGORA (posição decimal, 19,5 quando hoje é 19 ao meio-dia) no mês atual.
 * Início e fim são recortados a [1, axisEnd] — o período em andamento termina
 * em "agora", que no eixo é a borda direita. Sem min-width: a largura é a
 * duração sobre o calendário, e esticar para caber texto seria mentir.
 *
 * `trackPx` é a largura da trilha medida na tela: com ela se sabe quantos dos
 * três valores (consumer, líquido, rebate) cabem DENTRO do card. Quando não
 * cabe nenhum — período de horas —, o par líquido/rebate vai para fora, abaixo
 * do card. Dois períodos estreitos vizinhos teriam esses rótulos empilhados um
 * sobre o outro: vizinhos cujos centros distam menos que a largura do rótulo
 * formam um grupo e só o período mais largo do grupo mostra o seu — os demais
 * ficam com o tooltip, que é o que um período de minutos consegue carregar.
 */
const planRunBars = (runs, axisEnd, trackPx = 0) => {
    const barras = runs.map((run) => {
        const start = Clamp(Number(run.start), 1, axisEnd);
        const end = Clamp(Number(run.end), 1, axisEnd);
        const left = pctOf(start, axisEnd);
        const width = Math.max(0, pctOf(end, axisEnd) - left);
        return {
            run,
            left,
            width,
            dentro: quantosValoresCabem((width / 100) * trackPx),
            center: Clamp(left + width / 2, LABEL_MIN_PCT, LABEL_MAX_PCT),
            labelCenter: null,
        };
    });
    const semValorDentro = barras.filter((barra) => barra.dentro === 0);
    const grupos = [];
    for (const barra of semValorDentro) {
        const grupo = grupos[grupos.length - 1];
        if (grupo && barra.center - grupo[grupo.length - 1].center < LABEL_WIDTH_PCT) {
            grupo.push(barra);
        } else {
            grupos.push([barra]);
        }
    }
    for (const grupo of grupos) {
        const maisLarga = grupo.reduce((melhor, barra) => (barra.width > melhor.width ? barra : melhor), grupo[0]);
        maisLarga.labelCenter = maisLarga.center;
    }
    return barras;
};

const GRID = 'grid grid-cols-[200px_minmax(0,1fr)]';
const ROW = `${GRID} border-t border-mono-200 py-3 dark:border-mono-800`;

/**
 * Filtro de mês: ‹ quatro chips › e o botão "Hoje". Só meses com registro
 * ficam habilitados — de `minIndex` (mês em que a série começou) a `maxIndex`
 * (mês atual); as setas param nos limites e "Hoje" volta seleção e janela para
 * o mês atual. Botão em vez de select: é um só destino, e o botão fala a
 * mesma língua visual dos chips.
 */
const MonthFilter = ({ selected, minIndex, maxIndex, onSelect }) => {
    // Do mês atual para trás, até o mês em que a série do anúncio começou:
    // antes disso não há registro e depois não há calendário. O atual é a
    // primeira opção e o padrão, então voltar para "hoje" é escolher o topo.
    const meses = [];
    for (let index = maxIndex; index >= minIndex; index -= 1) {
        meses.push(index);
    }
    return (
        <select
            value={selected}
            onChange={(event) => onSelect(Number(event.target.value))}
            aria-label="Mês da linha do tempo"
            className="rounded-md border border-mono-300 bg-white px-2.5 py-1 text-xs font-medium tabular-nums text-mono-700 transition-colors hover:border-mono-400 focus:outline-none focus:ring-1 focus:ring-info-600/40 dark:border-mono-700 dark:bg-mono-950 dark:text-mono-200 dark:hover:border-mono-500"
        >
            {meses.map((index) => (
                <option key={index} value={index}>
                    {monthLabelOf(index)}
                </option>
            ))}
        </select>
    );
};

/**
 * Régua de dias: coluna esquerda vazia (alinha com os nomes das campanhas),
 * coluna direita com os rótulos posicionados por left:% e, abaixo, a linha de
 * 1px com ticks de 7px. `axisEnd` é onde o eixo termina (último dia do mês, ou
 * AGORA no mês atual) e `lastLabel` o rótulo da ponta (30, ou o dia de hoje).
 * No mês atual a borda direita é agora; o rótulo de hoje fica na posição do
 * dia, em destaque, e não existe linha vertical de "hoje".
 */
const DayRuler = ({ axisEnd, lastLabel, todayDay }) => {
    const marks = rulerDaysOf(lastLabel);
    return (
        <div className={GRID}>
            <div />
            <div className="relative h-7">
                {marks.map((day) => {
                    const hoje = IsNum(todayDay) && day === todayDay;
                    return (
                        <span
                            key={day}
                            className={`absolute top-0 -translate-x-1/2 font-mono text-[10px] tabular-nums ${
                                hoje ? 'font-semibold text-black dark:text-white' : 'text-mono-500 dark:text-mono-400'
                            }`}
                            style={{ left: `${pctOf(day, axisEnd)}%` }}
                            title={hoje ? 'Hoje' : undefined}
                        >
                            {Pad2(day)}
                        </span>
                    );
                })}
                <div className="absolute inset-x-0 bottom-[7px] h-px bg-mono-200 dark:bg-mono-700" />
                {marks.map((day) => (
                    <span
                        key={`tick-${day}`}
                        aria-hidden="true"
                        className="absolute bottom-0 h-[7px] w-px bg-mono-200 dark:bg-mono-700"
                        style={{ left: `${pctOf(day, axisEnd)}%` }}
                    />
                ))}
            </div>
        </div>
    );
};

// Um valor dentro do card: rótulo pequeno em cima, número embaixo. O líquido
// é o que o vendedor recebe — é ele que fica em destaque.
const ValorNoCard = ({ label, children, forte = false }) => (
    <div className="min-w-0">
        <div className="text-[9px] uppercase leading-tight tracking-wide text-mono-500 dark:text-mono-400">{label}</div>
        <div
            className={`truncate text-[13px] leading-tight tabular-nums ${
                forte ? 'font-semibold text-black dark:text-white' : 'text-mono-600 dark:text-mono-300'
            }`}
        >
            {children}
        </div>
    </div>
);

// Largura real do elemento, em px. Medir a trilha é o único jeito honesto de
// saber quanto espaço um período tem: a largura do card vem do calendário
// (uma % da trilha), não do conteúdo.
export function useLarguraPx(ref) {
    const [largura, setLargura] = useState(0);
    useLayoutEffect(() => {
        const alvo = ref.current;
        if (!alvo || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(([entrada]) => setLargura(entrada.contentRect.width));
        observer.observe(alvo);
        return () => observer.disconnect();
    }, [ref]);
    return largura;
}

/**
 * Um card = um período em que a campanha valeu no mês. A largura é a duração
 * sobre o calendário e os valores moram DENTRO dele (consumer, líquido,
 * rebate), na ordem em que o dono lê: o que o comprador pagou, o que sobrou
 * para o vendedor e o que o ML bancou.
 *
 * Período estreito não estica o card: os valores que não couberem saem, do
 * último para o primeiro, e o resto da informação (datas, duração, preço "de",
 * sua parte) fica na visão "Dados".
 */
const RunBar = ({ bar, campaign, selecionado, onSelect }) => {
    const { run, left, width, dentro, labelCenter } = bar;
    const liquido = money(run.liquido);
    const rebate = money(run.rebate);
    const consumer = money(run.consumer_price);
    const resumo = `${campaign.name} · ${runLabel(run)} · consumer ${consumer} · líquido ${liquido} · rebate ${rebate}${run.ongoing ? ' · ainda valendo' : ''}`;

    return (
        <>
            <button
                type="button"
                aria-pressed={selecionado}
                aria-label={`${resumo} · comparar com as demais campanhas`}
                onClick={() => onSelect(selecionado ? null : runKey(campaign, run))}
                className={`absolute top-0 flex h-14 items-center justify-center gap-4 overflow-hidden rounded-lg border px-2.5 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info-600/40 dark:focus-visible:ring-info-400/40 ${
                    selecionado
                        ? 'border-info-600 bg-info-600/10 dark:border-info-400 dark:bg-info-400/10'
                        : 'border-mono-200 bg-mono-50 hover:border-mono-400 dark:border-mono-700 dark:bg-mono-800 dark:hover:border-mono-500'
                }`}
                style={{ left: `${left}%`, width: `${width}%` }}
            >
                {dentro >= 3 ? <ValorNoCard label="Consumer">{consumer}</ValorNoCard> : null}
                {dentro >= 1 ? (
                    <ValorNoCard label="Líquido" forte>
                        {liquido}
                    </ValorNoCard>
                ) : null}
                {dentro >= 2 ? <ValorNoCard label="Rebate">{rebate}</ValorNoCard> : null}
            </button>
            {/* Período estreito: o par que não coube fica logo abaixo do card. */}
            {IsNum(labelCenter) ? (
                <div
                    className="absolute top-[60px] flex -translate-x-1/2 items-baseline gap-3 whitespace-nowrap"
                    style={{ left: `${labelCenter}%` }}
                >
                    <span className="inline-flex items-baseline gap-1">
                        <span className="text-[9px] uppercase text-mono-500 dark:text-mono-400">líq</span>
                        <span className="text-[13px] font-semibold tabular-nums text-black dark:text-white">
                            {liquido}
                        </span>
                    </span>
                    <span className="inline-flex items-baseline gap-1">
                        <span className="text-[9px] uppercase text-mono-500 dark:text-mono-400">reb</span>
                        <span className="text-[13px] tabular-nums text-mono-600 dark:text-mono-300">{rebate}</span>
                    </span>
                </div>
            ) : null}
        </>
    );
};

const CampaignRow = ({ campaign, axisEnd, month, selecionado, onSelect }) => {
    const trackRef = useRef(null);
    const trackPx = useLarguraPx(trackRef);
    const runs = Array.isArray(campaign.runs) ? campaign.runs : [];
    const periods = periodsLabel(runs, month);
    const bars = planRunBars(runs, axisEnd, trackPx);
    // A faixa cresce quando algum período é estreito demais para levar os
    // valores dentro do card: eles ficam logo abaixo dele.
    const temValorFora = bars.some((bar) => IsNum(bar.labelCenter));
    return (
        <div className={ROW}>
            <div className="min-w-0 pr-4">
                <div className="truncate text-sm font-semibold text-black dark:text-white" title={campaign.name}>
                    {campaign.name}
                </div>
                <div
                    className="mt-0.5 truncate font-mono text-xs text-mono-500 dark:text-mono-400"
                    title={`${campaign.campaign_id} · ${periods}`}
                >
                    {campaign.campaign_id} · {periods}
                </div>
            </div>
            <div ref={trackRef} className={`relative ${temValorFora ? 'h-[84px]' : 'h-14'}`}>
                {bars.map((bar) => (
                    <RunBar
                        key={bar.run.from || `${bar.run.start}-${bar.run.end}`}
                        bar={bar}
                        campaign={campaign}
                        selecionado={selecionado === runKey(campaign, bar.run)}
                        onSelect={onSelect}
                    />
                ))}
            </div>
        </div>
    );
};

// Diferença assinada, com a cor dizendo se é ganho ou perda para o vendedor.
const Delta = ({ valor, invertido = false }) => {
    if (!IsNum(valor) || Math.abs(valor) < 0.005) {
        return <span className="text-mono-400">igual</span>;
    }
    const ganho = invertido ? valor < 0 : valor > 0;
    return (
        <span
            className={`font-semibold tabular-nums ${
                ganho ? 'text-success-700 dark:text-success-400' : 'text-danger-700 dark:text-danger-400'
            }`}
        >
            {valor > 0 ? '+' : '−'}
            {FormatMoney(Math.abs(valor))}
        </span>
    );
};

/**
 * O que muda se outra campanha estiver valendo no lugar da clicada. O líquido
 * é o número que decide (é o que entra para o vendedor); consumer e rebate
 * ficam ao lado para explicar de onde vem a diferença.
 */
const ComparacaoPanel = ({ comparacao, onClose }) => {
    const { referencia, outras } = comparacao;
    return (
        <div className="mt-4 rounded-lg border border-info-600/40 bg-info-600/5 p-3 dark:border-info-400/40 dark:bg-info-400/5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-xs">
                    <span className="font-semibold text-black dark:text-white">{referencia.campaign.name}</span>
                    <span className="ml-1.5 font-mono text-mono-500 dark:text-mono-400">
                        {runLabel(referencia.run)} · líquido {money(referencia.run.liquido)}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-[11px] font-medium text-mono-500 hover:text-black dark:text-mono-400 dark:hover:text-white"
                >
                    fechar
                </button>
            </div>
            {outras.length === 0 ? (
                <p className="mt-2 text-[11px] text-mono-500 dark:text-mono-400">
                    Nenhuma outra campanha valeu neste mês para comparar.
                </p>
            ) : (
                <table className="mt-2 w-full text-xs">
                    <thead className="text-left text-[10px] uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        <tr>
                            <th className="py-1 font-semibold">Se estivesse valendo</th>
                            <th className="py-1 text-right font-semibold">Líquido</th>
                            <th className="py-1 text-right font-semibold">Consumer</th>
                            <th className="py-1 text-right font-semibold">Rebate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {outras.map(({ campaign, run, liquido, consumer, rebate }) => (
                            <tr
                                key={campaign.campaign_id}
                                className="border-t border-info-600/15 dark:border-info-400/15"
                            >
                                <td className="py-1.5 pr-3">
                                    <span className="font-medium text-black dark:text-white">{campaign.name}</span>
                                    <span className="ml-1.5 font-mono text-[10px] text-mono-500 dark:text-mono-400">
                                        {runLabel(run)}
                                    </span>
                                </td>
                                <td className="py-1.5 text-right">
                                    <Delta valor={liquido} />
                                    <span className="ml-1.5 font-mono text-[10px] text-mono-500 dark:text-mono-400">
                                        {money(run.liquido)}
                                    </span>
                                </td>
                                <td className="py-1.5 text-right">
                                    {/* Consumer maior é preço maior para o comprador: aí o verde é o menor. */}
                                    <Delta valor={consumer} invertido />
                                </td>
                                <td className="py-1.5 text-right">
                                    <Delta valor={rebate} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

// Três linhas fantasma com barra cinza, na mesma malha das linhas reais.
const SKELETON_BARS = [
    { left: 0, width: 32 },
    { left: 44, width: 20 },
    { left: 70, width: 30 },
];

const TimelineSkeleton = () => (
    <div aria-hidden="true">
        {SKELETON_BARS.map((bar) => (
            <div key={bar.left} className={ROW}>
                <div className="pr-4">
                    <Skeleton className="h-3.5 w-36" />
                    <Skeleton className="mt-1.5 h-3 w-44" />
                </div>
                <div className="relative h-14">
                    <div className="absolute inset-y-0" style={{ left: `${bar.left}%`, width: `${bar.width}%` }}>
                        <Skeleton className="h-full w-full rounded-lg" />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const Notice = ({ children, tone = 'muted' }) => (
    <div
        className={`border-t border-mono-200 py-8 text-center text-[13px] dark:border-mono-800 ${
            tone === 'danger' ? 'text-danger-600 dark:text-danger-400' : 'text-mono-500 dark:text-mono-400'
        }`}
    >
        {children}
    </div>
);

// Alternância de visualização, no padrão segmentado que o app já usa para
// isso (Garantia, Localizações do armazém): mesmos dados, duas leituras.
const VIEWS = [
    { id: 'barras', label: 'Barras' },
    { id: 'dados', label: 'Dados' },
];

const ViewSwitch = ({ view, onChange }) => (
    <div className="inline-flex shrink-0 rounded-lg border border-mono-300 p-0.5 dark:border-mono-700">
        {VIEWS.map((option) => (
            <button
                key={option.id}
                type="button"
                onClick={() => onChange(option.id)}
                aria-pressed={view === option.id}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    view === option.id
                        ? 'bg-black text-white dark:bg-white dark:text-black'
                        : 'text-mono-600 hover:bg-mono-100 dark:text-mono-400 dark:hover:bg-mono-900'
                }`}
            >
                {option.label}
            </button>
        ))}
    </div>
);

// Os períodos de TODAS as campanhas do mês numa lista só, em ordem
// cronológica: na tabela o mês se lê de cima para baixo como a barra se lê da
// esquerda para a direita. Um período pode repetir a campanha (saiu e voltou).
const flattenRuns = (campaigns) =>
    campaigns
        .flatMap((campaign) => (Array.isArray(campaign.runs) ? campaign.runs.map((run) => ({ campaign, run })) : []))
        .sort((a, b) => Number(a.run.start) - Number(b.run.start));

const TH = 'px-2 py-1.5 font-semibold';
const TD = 'px-2 py-1.5 align-top';

/**
 * A mesma informação das barras, em tabela: uma linha por período, com as
 * datas completas, a duração e os valores que o tooltip da barra carrega.
 * Espelha a tabela da aba Acompanhamento (mesmas colunas de dinheiro), mas com
 * a lógica daqui — só a campanha que estava valendo, uma por vez.
 */
const TimelineTable = ({ campaigns, month }) => {
    const linhas = flattenRuns(campaigns);
    return (
        <div className="overflow-x-auto border-t border-mono-200 dark:border-mono-800">
            <table className="w-full min-w-[720px] text-xs">
                <thead className="bg-mono-50 text-left uppercase tracking-wide text-mono-500 dark:bg-mono-950 dark:text-mono-400">
                    <tr>
                        <th className={TH}>Período</th>
                        <th className={TH}>Campanha</th>
                        <th className={`${TH} text-right`}>Consumer</th>
                        <th className={`${TH} text-right`}>De</th>
                        <th className={`${TH} text-right`}>Líquido</th>
                        <th className={`${TH} text-right`}>Rebate</th>
                        <th className={`${TH} text-right`}>Sua parte</th>
                    </tr>
                </thead>
                <tbody>
                    {linhas.map(({ campaign, run }) => (
                        <tr
                            key={`${campaign.campaign_id}-${run.from || run.start}`}
                            className="border-t border-mono-100 tabular-nums dark:border-mono-900"
                        >
                            <td className={`${TD} whitespace-nowrap font-mono text-mono-600 dark:text-mono-400`}>
                                {Pad2(run.start_day)}–{Pad2(run.end_day)}/{Pad2(month)}
                                <div className="text-[10px] text-mono-500 dark:text-mono-500">
                                    {/* O período INTEIRO, não a fatia do mês: a campanha pode ter começado antes do dia 1º. */}
                                    {FormatDateTimeBr(run.period_from ?? run.from)}
                                    {' → '}
                                    {run.ongoing ? 'agora' : FormatDateTimeBr(run.period_to ?? run.to)}
                                    {' · '}
                                    {FormatDurationHuman(run.duration_minutes)}
                                </div>
                            </td>
                            <td className={`${TD} min-w-[180px]`}>
                                <div className="font-medium text-black dark:text-white">{campaign.name}</div>
                                <div className="font-mono text-[10px] text-mono-500 dark:text-mono-400">
                                    {campaign.campaign_id}
                                    {campaign.type ? ` · ${GetCampaignTypeLabel(campaign.type)}` : ''}
                                </div>
                            </td>
                            <td className={`${TD} text-right font-mono`}>{money(run.consumer_price)}</td>
                            <td className={`${TD} text-right font-mono text-mono-500 dark:text-mono-400`}>
                                {money(run.regular_price)}
                            </td>
                            <td className={`${TD} text-right font-mono font-semibold`}>{money(run.liquido)}</td>
                            <td className={`${TD} text-right font-mono`}>
                                {money(run.rebate)}
                                {IsNum(run.meli_percentage) ? (
                                    <span className="ml-1 text-[10px] text-mono-400">
                                        {FormatPercentOrDash(run.meli_percentage)}
                                    </span>
                                ) : null}
                            </td>
                            <td className={`${TD} text-right font-mono`}>
                                {FormatPercentOrDash(run.seller_percentage)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const CampaignItemTimeline = ({ itemId }) => {
    // Hoje em Brasília, a cada render: é o teto do filtro e o mês em que a
    // régua termina em hoje. Barato, e a virada do mês não depende de remontar.
    const hojeSp = saoPauloParts(new Date());
    const currentIndex = toMonthIndex(hojeSp.year, hojeSp.month);

    // Filtro de mês é local ao componente; default = mês atual.
    const [selecionado, setSelecionado] = useState(currentIndex);
    const [view, setView] = useState('barras');
    // Período clicado nas barras: vira a referência da comparação com as
    // demais campanhas do mês. Trocar de mês, de anúncio ou de visão limpa.
    const [comparando, setComparando] = useState(null);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    // Início da série do anúncio = piso do filtro. Fica fora de `data` porque é
    // do ANÚNCIO, não do mês: trocar de mês zera `data`, e o filtro não pode
    // piscar desabilitado a cada busca. `since` undefined = nenhuma resposta
    // ainda (só o mês atual habilitado); null = série vazia (idem).
    const [recorded, setRecorded] = useState({ itemId: null, since: undefined });

    const monthKey = monthKeyOf(selecionado);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        // Zera a resposta anterior: `today_day`/`days_in_month` de outro mês não
        // podem vestir a régua do mês novo enquanto a busca não volta.
        setData(null);
        setComparando(null);
        api.getItemMonthlyTimeline(itemId, { month: monthKey })
            .then((res) => {
                if (!cancelled) {
                    setData(res);
                    setRecorded({ itemId, since: res?.recorded_since ?? null });
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar a linha do tempo.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [itemId, monthKey]);

    // Piso do filtro: o mês (Brasília) em que a série começou, nunca depois do
    // atual. Sem resposta ainda, ou série vazia, só o mês atual existe.
    const since = recorded.itemId === itemId ? recorded.since : undefined;
    const sinceMs = since ? Date.parse(since) : Number.NaN;
    const minIndex = Number.isFinite(sinceMs) ? Math.min(monthIndexAt(new Date(sinceMs)), currentIndex) : currentIndex;

    const select = (index) => setSelecionado(Clamp(index, minIndex, currentIndex));

    const { month } = fromMonthIndex(selecionado);
    const days = IsNum(data?.days_in_month) ? Number(data.days_in_month) : daysInMonthOf(selecionado);
    // Mês atual: o eixo termina em hoje. `today_day` vem do backend (relógio do
    // servidor); enquanto a resposta não chega, o dia de hoje em Brasília pelo
    // relógio local segura a régua no lugar e ela não pula quando a busca volta.
    const todayDay = IsNum(data?.today_day) ? Number(data.today_day) : selecionado === currentIndex ? hojeSp.day : null;
    const endsToday = IsNum(todayDay);
    // No mês atual o eixo termina em AGORA (posição decimal: 19,5 = meio-dia
    // do 19), não em 00:00 de hoje — senão tudo o que aconteceu hoje encosta
    // na borda direita com largura zero, e no dia 1º a régua fica sem o "01".
    // `now_day` vem do backend; enquanto a resposta não chega, o relógio local
    // (dia + fração) segura a régua no lugar. Mínimo 1,05 para o eixo ter
    // largura no primeiro minuto do mês.
    const nowDay = IsNum(data?.now_day) ? Number(data.now_day) : todayDay !== null ? todayDay + hojeSp.fraction : null;
    const axisEnd = endsToday ? Math.max(MIN_AXIS_END, Math.min(nowDay ?? todayDay, days + 1)) : days;
    const lastLabel = endsToday ? todayDay : days;
    const campaigns = Array.isArray(data?.campaigns) ? data.campaigns : [];

    let body;
    if (loading) {
        body = <TimelineSkeleton />;
    } else if (error) {
        body = <Notice tone="danger">{error}</Notice>;
    } else if (data && data.history_available === false) {
        body = <Notice>Linha do tempo indisponível nesta base: falta a série de preço.</Notice>;
    } else if (campaigns.length === 0) {
        body = (
            <Notice>
                Nenhuma campanha registrada neste mês.
                {/* No 1º mês da série o vazio pode ser só "começou a gravar no dia 18": vale dizer desde quando existe registro. */}
                {selecionado === minIndex && since ? (
                    <div className="mt-1 text-[11px]">O registro começou em {FormatDateTimeBr(since)}.</div>
                ) : null}
            </Notice>
        );
    } else if (view === 'dados') {
        body = <TimelineTable campaigns={campaigns} month={month} />;
    } else {
        const comparacao = comparando ? compararCampanhas(campaigns, comparando) : null;
        body = (
            <>
                {campaigns.map((campaign) => (
                    <CampaignRow
                        key={campaign.campaign_id}
                        campaign={campaign}
                        axisEnd={axisEnd}
                        month={month}
                        selecionado={comparando}
                        onSelect={setComparando}
                    />
                ))}
                {comparacao ? (
                    <ComparacaoPanel comparacao={comparacao} onClose={() => setComparando(null)} />
                ) : (
                    <p className="mt-3 text-[11px] text-mono-500 dark:text-mono-400">
                        Clique num período para comparar com as demais campanhas do mês.
                    </p>
                )}
            </>
        );
    }

    return (
        <section className="rounded-[10px] border border-mono-200 bg-white px-6 pb-6 pt-5 dark:border-mono-800 dark:bg-mono-900">
            <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-mono-500 dark:text-mono-400">
                    Linha do tempo de campanhas
                </h3>
                <div className="flex flex-wrap items-center gap-3">
                    <ViewSwitch
                        view={view}
                        onChange={(proxima) => {
                            setComparando(null);
                            setView(proxima);
                        }}
                    />
                    <MonthFilter selected={selecionado} minIndex={minIndex} maxIndex={currentIndex} onSelect={select} />
                </div>
            </header>
            {/* A régua de dias é a escala das barras; na tabela cada linha traz a própria data. */}
            {view === 'barras' ? (
                <DayRuler axisEnd={axisEnd} lastLabel={lastLabel} todayDay={endsToday ? todayDay : null} />
            ) : null}
            {body}
        </section>
    );
};

export default CampaignItemTimeline;
